import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  RefreshCw, Plus, X, Search, Shield, TrendingUp, Headphones, UserCheck, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTAL_TYPES = [
  { value: 'investor', label: 'Investor Portal', icon: TrendingUp, color: 'bg-emerald-100 text-emerald-600' },
  { value: 'team', label: 'Team Portal', icon: Users, color: 'bg-blue-100 text-blue-600' },
  { value: 'network', label: 'Network Portal', icon: UserCheck, color: 'bg-purple-100 text-purple-600' },
  { value: 'admin', label: 'Admin Portal', icon: Shield, color: 'bg-red-100 text-red-600' },
];

const PortalAccessTab = () => {
  const [selectedPortal, setSelectedPortal] = useState('investor');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/portal-access/${selectedPortal}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (err) {
      console.error('Failed to load portal members:', err);
    }
    setLoading(false);
  }, [selectedPortal]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/admin/users/search-all?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch (err) { console.error(err); }
    setSearching(false);
  };

  const handleAddMember = async (user) => {
    if (members.some(m => m.user_id === user.user_id)) {
      toast.error('User already has access');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/portal-access/add`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ portal_type: selectedPortal, user_id: user.user_id })
      });
      if (res.ok) {
        toast.success(`Added ${user.email} to ${selectedPortal} portal`);
        setSearchQuery('');
        setSearchResults([]);
        loadMembers();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add');
      }
    } catch (err) {
      toast.error('Failed to add member');
    }
    setSaving(false);
  };

  const handleRemoveMember = async (member) => {
    const userName = member.user?.name || member.user?.email || member.user_id;
    if (!window.confirm(`Remove "${userName}" from ${selectedPortal} portal?`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/portal-access/${selectedPortal}/${member.user_id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.ok) {
        toast.success(`Removed from ${selectedPortal} portal`);
        loadMembers();
      }
    } catch (err) {
      toast.error('Failed to remove');
    }
    setSaving(false);
  };

  const handleToggleAdvocate = async (member) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/portal-access/${selectedPortal}/${member.user_id}/advocate`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ is_advocate: !member.is_advocate })
      });
      if (res.ok) {
        toast.success(member.is_advocate ? 'Advocate status removed' : 'Marked as advocate');
        loadMembers();
      }
    } catch (err) {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  const portalConfig = PORTAL_TYPES.find(p => p.value === selectedPortal);
  const PortalIcon = portalConfig?.icon || Shield;

  return (
    <Card className="shadow-lg border-gray-200">
      <CardContent className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Select value={selectedPortal} onValueChange={setSelectedPortal}>
              <SelectTrigger className="w-[200px]" data-testid="portal-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PORTAL_TYPES.map(pt => (
                  <SelectItem key={pt.value} value={pt.value}>
                    <span className="flex items-center gap-2">
                      <pt.icon className="w-4 h-4" />
                      {pt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadMembers}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <Badge className={portalConfig?.color || 'bg-gray-100'}>{members.length} members</Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Members List */}
          <div>
            <Label className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
              <PortalIcon className="w-3.5 h-3.5" />
              {portalConfig?.label} Members
            </Label>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-2 mt-3 max-h-[400px] overflow-y-auto">
                {members.map((member) => {
                  const user = member.user || {};
                  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || member.user_id;
                  return (
                    <div key={member.user_id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{displayName}</p>
                          <p className="text-[10px] text-gray-400">{user.email || ''} {user.company ? `• ${user.company}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant={member.is_advocate ? 'default' : 'outline'}
                          size="sm"
                          className={`h-6 text-[10px] px-2 ${member.is_advocate ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                          onClick={() => handleToggleAdvocate(member)}
                          disabled={saving}
                          data-testid={`toggle-advocate-${member.user_id}`}
                        >
                          <UserCheck className="w-3 h-3 mr-0.5" />
                          {member.is_advocate ? 'Advocate' : 'Set Advocate'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                          onClick={() => handleRemoveMember(member)}
                          disabled={saving}
                          data-testid={`remove-portal-member-${member.user_id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {members.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No members in this portal</p>
                    <p className="text-xs">Search and add users below</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add Member Search */}
          <div>
            <Label className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add Member
            </Label>
            <p className="text-xs text-gray-400 mb-3">
              Search by name, email, or company. Adding a user syncs to their /me/settings Quick Access.
            </p>
            <div className="relative mt-3">
              <div className="flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => searchUsers(e.target.value)}
                  placeholder="Search users by name or email..."
                  className="text-sm"
                  data-testid="portal-member-search"
                />
                {searching && <RefreshCw className="w-3 h-3 animate-spin text-gray-400 absolute right-2 top-1/2 -translate-y-1/2" />}
              </div>
              {searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((user) => {
                    const alreadyMember = members.some(m => m.user_id === user.user_id);
                    return (
                      <button
                        key={user.user_id}
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between border-b last:border-b-0 ${alreadyMember ? 'bg-gray-50 text-gray-400' : 'hover:bg-blue-50'}`}
                        onClick={() => !alreadyMember && handleAddMember(user)}
                        disabled={saving || alreadyMember}
                      >
                        <div>
                          <p className="font-medium">{user.name || `${user.first_name || ''} ${user.last_name || ''}`}</p>
                          <p className="text-xs text-gray-400">{user.email} {user.company ? `• ${user.company}` : ''}</p>
                        </div>
                        {alreadyMember ? (
                          <Badge variant="outline" className="text-[10px]">Added</Badge>
                        ) : (
                          <Plus className="w-3 h-3 text-blue-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Portal Info */}
            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-xs font-semibold text-gray-600 mb-2">How Portal Access Works</h4>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>- Adding a user syncs a portal tag to their profile</li>
                <li>- The portal appears in their /me/settings Quick Access</li>
                <li>- No NDA or intake required (pilot programs only)</li>
                <li>- Support portal houses Team, Investor & Network sub-views</li>
                <li>- Advocates get special tracking for referrals</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortalAccessTab;
